import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import AppLayout from '../components/AppLayout';
import './History.css';

function formatAmount(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const LIMIT = 20;

const TYPE_LABELS = {
  debit: 'Debit',
  credit: 'Credit',
  transfer: 'Virement',
  deposit: 'Depot',
  withdrawal: 'Retrait',
  salary: 'Salaire',
};

const FILTERS = [
  { key: 'all',        label: 'Toutes',       icon: 'fa-list' },
  { key: 'transfer',   label: 'Virements',    icon: 'fa-right-left' },
  { key: 'salary',     label: 'Salaires',     icon: 'fa-briefcase' },
  { key: 'bonus',      label: 'Bonus',        icon: 'fa-star' },
  { key: 'deposit',    label: 'Depots',       icon: 'fa-arrow-down-to-line' },
  { key: 'withdrawal', label: 'Retraits',     icon: 'fa-arrow-up-from-line' },
  { key: 'income',     label: 'Autres revenus', icon: 'fa-circle-plus' },
];

export default function History() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimeout = React.useRef(null);

  const fetchTransactions = useCallback(async (off = 0, type = filter, q = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: off, type: type });
      if (q.trim().length >= 2) params.set('search', q.trim());
      const res = await axios.get(`/api/bank/transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTransactions(res.data.transactions || []);
      setTotal(res.data.total || 0);
      setOffset(off);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [token, filter, search]);

  useEffect(() => { fetchTransactions(0, filter, search); }, [filter, search]); // eslint-disable-line

  const handleFilter = (key) => {
    setFilter(key);
    setOffset(0);
  };

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearch(val);
      setOffset(0);
    }, 400);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setOffset(0);
  };

  const filtered = transactions;

  return (
    <AppLayout>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <i className="fa-solid fa-arrow-left" />
        </button>
        <div>
          <h1 className="page-title">Historique des transactions</h1>
          <p className="page-subtitle">{total} transaction{total > 1 ? 's' : ''} au total</p>
        </div>
      </div>

      {/* Filters */}
      <div className="history-filters">
        {FILTERS.map(f => (
          <button
            key={f.key}
            className={`filter-btn ${filter === f.key ? 'active' : ''}`}
            onClick={() => handleFilter(f.key)}
          >
            <i className={`fa-solid ${f.icon}`} />
            {f.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="history-search-bar">
        <i className="fa-solid fa-magnifying-glass history-search-icon" />
        <input
          className="history-search-input"
          type="text"
          placeholder="Rechercher par nom, IBAN, montant..."
          value={searchInput}
          onChange={handleSearchInput}
        />
        {searchInput && (
          <button className="history-search-clear" onClick={clearSearch}>
            <i className="fa-solid fa-xmark" />
          </button>
        )}
      </div>

      {/* Transaction list */}
      <div className="card history-card">
        {loading ? (
          <div className="history-loading">
            <i className="fa-solid fa-spinner spinner" style={{ fontSize: 28, color: 'var(--primary)' }} />
            <span>Chargement...</span>
          </div>
        ) : error ? (
          <div className="history-error">
            <i className="fa-solid fa-circle-exclamation" />
            <p>{error}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <i className="fa-solid fa-receipt" />
            <p>Aucune transaction</p>
            <span>Vos transactions apparaitront ici</span>
          </div>
        ) : (
          <div className="history-list">
            {filtered.map((tx) => {
              // Débit si c'est nous qui envoyons (from_iban = notre IBAN) ou type explicite debit/withdrawal
              const myIban = user?.iban;
              const myName = tx.character_name || 'Moi';
              const isSender = myIban && tx.from_iban === myIban;
              const isDebit = tx.type === 'debit' || tx.type === 'withdrawal' || (tx.type === 'transfer' && isSender);
              const counterpartIban = isDebit ? tx.to_iban : tx.from_iban;
              // Formater l'IBAN en nom court lisible
              const formatIban = (iban) => {
                if (!iban) return null;
                // Enlever les espaces, garder les 8 derniers chars avec préfixe
                const clean = iban.replace(/\s/g, '');
                return clean.length > 8 ? clean.slice(0, 2) + '…' + clean.slice(-6) : clean;
              };
              const counterpartName = tx.counterpart_name || formatIban(counterpartIban) || 'Système';
              // Titre directionnel
              let senderName, receiverName;
              if (tx.type === 'salary' || tx.type === 'credit' || (!isSender && tx.type === 'transfer')) {
                senderName = counterpartName;
                receiverName = myName;
              } else {
                senderName = myName;
                receiverName = counterpartName;
              }
              // Masquer le label s'il est redondant (ancien format FiveM "X → Y") ou générique
              const autoLabels = Object.values(TYPE_LABELS);
              const isRedundantLabel = !tx.label
                || tx.label.includes('→')
                || tx.label.includes('->')
                || autoLabels.includes(tx.label);
              return (
                <div key={tx.id} className="history-row">
                  <div className={`history-icon ${isDebit ? 'tx-debit' : 'tx-credit'}`}>
                    <i className={`fa-solid ${isDebit ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
                  </div>
                  <div className="history-info">
                    <span className="history-label">
                      <span className="tx-party">{senderName}</span>
                      <i className="fa-solid fa-arrow-right tx-arrow" />
                      <span className="tx-party">{receiverName}</span>
                    </span>
                    {!isRedundantLabel && (
                      <span className="tx-description">{tx.label}</span>
                    )}
                    <div className="history-meta">
                      <span className="history-date">{formatDate(tx.created_at)}</span>
                      {tx.source === 'fivem' && (
                        <span className="history-source">
                          <i className="fa-solid fa-gamepad" /> In-game
                        </span>
                      )}
                      {tx.source === 'web' && (
                        <span className="history-source">
                          <i className="fa-solid fa-globe" /> Web
                        </span>
                      )}
                      {tx.source === 'web_pending' && (
                        <span className="history-source history-source-pending">
                          <i className="fa-solid fa-clock" /> En attente
                        </span>
                      )}
                      {tx.id && (
                        <span className="tx-id" title={tx.id}>
                          <i className="fa-solid fa-hashtag" />{tx.id.slice(0, 8)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="history-right">
                    <span className={`history-amount ${isDebit ? 'amount-debit' : 'amount-credit'}`}>
                      {isDebit ? '-' : '+'}{formatAmount(Math.abs(tx.amount))}
                    </span>
                    {tx.balance_after !== null && tx.balance_after !== undefined && (
                      <span className="history-balance-after">
                        Solde: {formatAmount(tx.balance_after)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!loading && total > LIMIT && (
          <div className="history-pagination">
            <button
              className="btn-secondary pagination-btn"
              onClick={() => fetchTransactions(Math.max(0, offset - LIMIT), filter)}
              disabled={offset === 0}
            >
              <i className="fa-solid fa-chevron-left" />
              Precedent
            </button>
            <span className="pagination-info">
              {Math.floor(offset / LIMIT) + 1} / {Math.ceil(total / LIMIT)}
            </span>
            <button
              className="btn-secondary pagination-btn"
              onClick={() => fetchTransactions(offset + LIMIT, filter)}
              disabled={offset + LIMIT >= total}
            >
              Suivant
              <i className="fa-solid fa-chevron-right" />
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
